import AddIcon from '@mui/icons-material/Add';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { Button, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import { differenceInDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import { FC, memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router';
import { ShipmentOrderForm } from 'src/components/V2ShipmentOrderForm';
import LicenseContext from 'src/contexts/LicenseContext';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { buildTruckShiftsPath } from 'src/pages/TruckShifts/truckShiftGrid';
import { PlanningViewKind } from 'src/vo/PlanningViewKind.vo';

type Props = {
  startOn: string;
  endOn: string;
  isLoading: boolean;
  planningButtonOnClick: () => void;
  selectImportMethodButtonOnClick: () => void;
  estimationButtonOnClick: () => void;
  allocateHistoriesOnClick: () => void;
  deliveryIdSelected: boolean;
  allocateHistoriesForCompany: AllocateHistoryEntity[];
  currentHistoryVersion: string | undefined;
  resetAllocateHistories: () => void;
  setScenarioPlanningDialogIsOpen: (open: boolean) => void;

  currentView: PlanningViewKind;
  updateView: (val: PlanningViewKind) => void;
  screenName: string;
}

const ActionsPresenter: FC<Props> = memo((
  {
    startOn,
    endOn,
    isLoading,
    planningButtonOnClick,
    selectImportMethodButtonOnClick,
    deliveryIdSelected,
    estimationButtonOnClick,
    allocateHistoriesOnClick,
    allocateHistoriesForCompany,
    currentHistoryVersion,
    resetAllocateHistories,
    setScenarioPlanningDialogIsOpen,

    currentView,
    updateView,
    screenName,
  }
) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const licenseContext = useContext(LicenseContext);

  const [estimationTooltipTitle, setEstimationTooltipTitle] = useState<ReactElement | string>('');
  const [planningTooltipTitle, setPlanningTooltipTitle] = useState<ReactElement | string>('');
  const [historyTooltipTitle, setHistoryTooltipTitle] = useState<ReactElement | string>('');
  const [scenarioPlanningTitle, setScenarioPlanningTitle] = useState<ReactElement | string>('');

  const [useAlgorithmPlanning, setUseAlgorithmPlanning] = useState(false);

  useEffect(() => {
    if (!licenseContext || !licenseContext.config) return;

    setUseAlgorithmPlanning(licenseContext.config.use_algorithm_planning);
  }, [licenseContext]);

  const [newStartOn, setNewStartOn] = useState<Date | undefined>();
  const [newEndOn, setNewEndOn] = useState<Date | undefined>();
  const [selectedRange, setSelectedRange] = useState<DateRange<Date>>([
    new Date(startOn),
    new Date(endOn)
  ]);

  useEffect(() => {
    setSelectedRange([
      new Date(startOn),
      new Date(endOn)
    ]);
  }, [startOn, endOn]);

  const handleDateRange = useCallback((range: DateRange<Date>) => {
    ReactGA.event('apply', { screen_name: screenName, button_name: '日付選択', label: range });
    setSelectedRange((prev) => [
      range[0] || prev[0],
      range[1] || prev[1],
    ]);
    const start = range[0];
    const end = range[1];
    if (!start || !end) return;

    const diffDays = differenceInDays(end, start);

    if (diffDays < 0) {
      enqueueSnackbar('終了日は開始日よりも過去に設定できません');
      return;
    }

    if (diffDays > 9) {
      enqueueSnackbar('最大取得可能日数の10日を超過しています');
      return;
    }

    setNewStartOn(start);
    setNewEndOn(end);
  }, [enqueueSnackbar, screenName]);

  useEffect(() => {
    if (newStartOn && newEndOn) {
      const path = [
        currentView === 'timeline' ? 'timeline' : 'plans',
        format(newStartOn, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' }),
        format(newEndOn, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' })
      ].join('/');

      void navigate(`/${path}`);
    }
  }, [newStartOn, newEndOn, navigate, currentView]);

  const noDeliveryIdsSelected = useMemo(() => !deliveryIdSelected, [deliveryIdSelected]);

  useEffect(() => {
    if (noDeliveryIdsSelected) {
      setEstimationTooltipTitle((
        <>
          配送計画を選択してください。
          <br />
          選択された配送計画の見積りを表示します。
        </>
      ));
      return;
    }

    setEstimationTooltipTitle(
      (
        <>
          選択された配送計画の見積りを表示します。
        </>
      )
    );
  }, [noDeliveryIdsSelected]);

  useEffect(() => {
    if (useAlgorithmPlanning) {
      if (licenseContext.config?.selected_company_id === 0) {
        setScenarioPlanningTitle('配車対象の事業所を選択してください');
        setPlanningTooltipTitle('配車対象の事業所を選択してください');
        setHistoryTooltipTitle('事業所を選択してください');
      } else {
        setScenarioPlanningTitle('シナリオ配車する');
        setPlanningTooltipTitle('自動配車する');
        setHistoryTooltipTitle('元に戻す');
      }
    } else {
      setScenarioPlanningTitle('自動配車を利用したい場合はセールス (sales@logpose.co.jp) へご連絡ください。');
      setPlanningTooltipTitle('自動配車を利用したい場合はセールス (sales@logpose.co.jp) へご連絡ください。');
      setHistoryTooltipTitle('自動配車を利用したい場合はセールス (sales@logpose.co.jp) へご連絡ください。');
    }
  }, [useAlgorithmPlanning, licenseContext.config?.selected_company_id]);

  const datePickMemo = useMemo(() => (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      marginY={0.5}
      marginLeft={1}
    >
      <DateRangePicker
        value={selectedRange}
        onOpen={() => ReactGA.event('click', { screen_name: screenName, button_name: '日付選択' })}
        onChange={handleDateRange}
        slotProps={{
          textField: { size: 'small', sx: { p: 0, m: 0, } }
        }}
      />
    </Stack>
  ), [selectedRange, handleDateRange, screenName]);

  const disablePlanning = useMemo(() => (
    isLoading || !useAlgorithmPlanning || !!currentHistoryVersion || licenseContext?.config?.selected_company_id === 0
  ), [currentHistoryVersion, isLoading, licenseContext.config, useAlgorithmPlanning]);

  const shiftButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: screenName, button_name: '勤務計画' });

    void navigate(`/shifts/${startOn}/${endOn}/${currentView === 'timeline' ? 'timeline' : ''}`);
  }, [endOn, navigate, startOn, currentView, screenName]);

  const shiftButtonMemo = useMemo(() => (
    <Button
      variant="outlined"
      onClick={shiftButtonOnClick}
      disabled={isLoading || !!currentHistoryVersion}
      sx={{ borderRadius: '5px', height: '35px', p: 1.5, }}
    >
      <Typography variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: '95%' }}>
        勤務計画
      </Typography>
    </Button>
  ), [currentHistoryVersion, isLoading, shiftButtonOnClick]);

  const truckShiftButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: screenName, button_name: '稼働表' });

    // 呼び出し元の範囲(startOn/endOn)をそのまま渡す。稼働表側は startOn を anchor に週/月へ正規化して表示し、
    // 「戻る」ではこの範囲へ戻す（勤務計画ボタンと同じ挙動）。backTo に呼び出し元画面を渡す。
    void navigate(buildTruckShiftsPath({ startOn, endOn, backTo: currentView === 'timeline' ? 'timeline' : 'plans' }));
  }, [endOn, navigate, startOn, currentView, screenName]);

  const truckShiftButtonMemo = useMemo(() => (
    <Button
      variant="outlined"
      onClick={truckShiftButtonOnClick}
      disabled={isLoading || !!currentHistoryVersion}
      sx={{ borderRadius: '5px', height: '35px', p: 1.5, }}
    >
      <Typography variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: '95%' }}>
        稼働表
      </Typography>
    </Button>
  ), [currentHistoryVersion, isLoading, truckShiftButtonOnClick]);

  const onClickScenarioPlanning = useCallback(() => {
    ReactGA.event('click', { screen_name: screenName, button_name: 'シナリオ配車' });
    setScenarioPlanningDialogIsOpen(true);
  }, [setScenarioPlanningDialogIsOpen, screenName]);

  const scenarioPlanningMemo = useMemo(() => (
    <Tooltip
      title={scenarioPlanningTitle}
      arrow
    >
      <span>
        <IconButton
          onClick={onClickScenarioPlanning}
          disabled={isLoading || !useAlgorithmPlanning || (licenseContext?.config?.selected_company_id === 0) || !setScenarioPlanningDialogIsOpen}
          sx={{
            backgroundColor: theme.colors.alpha.trueWhite[100]
          }}
        >
          <PlayCircleIcon />
        </IconButton>
      </span>
    </Tooltip>
  ), [isLoading, licenseContext?.config?.selected_company_id, scenarioPlanningTitle, onClickScenarioPlanning, theme.colors.alpha.trueWhite, useAlgorithmPlanning, setScenarioPlanningDialogIsOpen]);

  const historyMemo = useMemo(() => (
    <Tooltip
      title={historyTooltipTitle}
      arrow
    >
      <span>
        <IconButton
          onClick={allocateHistoriesOnClick}
          disabled={isLoading || !useAlgorithmPlanning || !allocateHistoriesForCompany?.length || (licenseContext?.config?.selected_company_id === 0) || !allocateHistoriesOnClick}
          sx={{
            backgroundColor: theme.colors.alpha.trueWhite[100]
          }}
        >
          <HistoryRoundedIcon />
        </IconButton>
      </span>
    </Tooltip>
  ), [allocateHistoriesForCompany?.length, allocateHistoriesOnClick, historyTooltipTitle, isLoading, licenseContext?.config?.selected_company_id, theme.colors.alpha.trueWhite, useAlgorithmPlanning]);

  // const estimateMemo = useMemo(() => (
  //   <Tooltip
  //     title={estimationTooltipTitle}
  //     arrow
  //   >
  //     <span>
  //       <IconButton
  //         onClick={estimationButtonOnClick}
  //         disabled={isLoading || noDeliveryIdsSelected || !!currentHistoryVersion || !estimationButtonOnClick}
  //         sx={{
  //           backgroundColor: theme.colors.alpha.trueWhite[100]
  //         }}
  //       >
  //         <CurrencyYenRoundedIcon />
  //       </IconButton>
  //     </span>
  //   </Tooltip>
  // ), [currentHistoryVersion, estimationButtonOnClick, estimationTooltipTitle, isLoading, noDeliveryIdsSelected, theme.colors.alpha.trueWhite]);

  const planningMemo = useMemo(() => (
    <Tooltip
      title={planningTooltipTitle}
      arrow
    >
      <span>
        <Button
          onClick={planningButtonOnClick}
          disabled={disablePlanning || !planningButtonOnClick}
          variant="contained"
          sx={{ borderRadius: '5px', height: '35px', p: 1.5, }}
        >
          <Typography variant="body1" sx={{ whiteSpace: 'noWrap', fontSize: '95%' }}>
            自動配車
          </Typography>
        </Button>
      </span>
    </Tooltip>
  ), [disablePlanning, planningButtonOnClick, planningTooltipTitle]);

  const importMemo = useMemo(() => {
    if (currentView === 'list') return null;

    return (
      <Button
        variant="outlined"
        disabled={isLoading || !!currentHistoryVersion || !selectImportMethodButtonOnClick}
        onClick={selectImportMethodButtonOnClick}
        sx={{ borderRadius: '5px', height: '35px', p: 1.5, }}
      >
        <Typography variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: '95%' }}>
          インポート
        </Typography>
      </Button>
    );
  }, [currentHistoryVersion, currentView, isLoading, selectImportMethodButtonOnClick]);

  const [newShipmentDialogOpen, setNewShipmentDialogOpen] = useState<boolean>(false);

  const onClickOpenCreateOrderDialog = useCallback(() => {
    ReactGA.event('click', { screen_name: screenName, button_name: '案件作成' });
    setNewShipmentDialogOpen(true);
  }, []);

  const dialogOnClose = useCallback(() => {
    setNewShipmentDialogOpen(false);
  }, []);

  const newOrderFormMemo = useMemo(() => {
    if (currentView === 'list') return null;

    return (
      <Button
        variant="outlined"
        onClick={onClickOpenCreateOrderDialog}
        disabled={isLoading || !!currentHistoryVersion}
        sx={{ borderRadius: '5px', height: '35px', p: 1.5, }}
      >
        <Typography variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: '95%' }}>
          案件作成
        </Typography>
        <AddIcon fontSize="small" />
      </Button>
    );
  }, [currentView, onClickOpenCreateOrderDialog, currentHistoryVersion, isLoading]);

  const dialogOnCloseWithResetAllocateHistories = useCallback(() => {
    resetAllocateHistories();
    setNewShipmentDialogOpen(false);
  }, [resetAllocateHistories]);

  const shipmentOrderFormDialogMemo = useMemo(() => {
    if (!startOn || !endOn || !newShipmentDialogOpen) return null;

    return (
      <ShipmentOrderForm
        baseDate={new Date(startOn)}
        startOn={startOn}
        newShipmentDialogOpen={newShipmentDialogOpen}
        setNewShipmentDialogOpen={setNewShipmentDialogOpen}
      />
    );
  }, [newShipmentDialogOpen, startOn, endOn]);

  // const selectViewMemo = useMemo(() => (
  //   <div style={{ textAlign: 'center', paddingTop: '1px' }}>
  //     <ToggleButtonGroup
  //       value={currentView}
  //       exclusive
  //       onChange={(_, val: PlanningViewKind) => {
  //         if (val === null) return;
  //         updateView(val);
  //       }}
  //       sx={{
  //         borderRadius: '20px',
  //         overflow: 'hidden',
  //         boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.2)',
  //       }}
  //     >
  //       <ToggleButton value="map" sx={{ padding: '3px 6px' }}>
  //         <MapIcon />
  //       </ToggleButton>
  //       <ToggleButton value="list" sx={{ padding: '3px 6px' }}>
  //         <FormatListBulletedIcon />
  //       </ToggleButton>
  //     </ToggleButtonGroup>
  //   </div>
  // ), [currentView, updateView]);

  return (
    <Paper>
      <Stack
        direction="row"
        gap={2}
        alignItems="center"
      >
        {shipmentOrderFormDialogMemo}
        <Stack
          alignItems="center"
          direction="row"
          gap={1}
          width="100%"
          flexGrow={1}
        >
          {datePickMemo}
          {shiftButtonMemo}
          {truckShiftButtonMemo}
          {/* {currentView !== 'timeline' && selectViewMemo} */}
        </Stack>
        <Stack
          direction="row"
          gap={2}
          sx={{ px: 1 }}
        >
          {licenseContext.config?.use_scenario_planning && scenarioPlanningMemo}
          {/* {historyMemo}
          {estimateMemo} */}
          {planningMemo}
          {importMemo}
          {newOrderFormMemo}
        </Stack>
      </Stack>
    </Paper>
  );
});

export default ActionsPresenter;
